import styled, { StyledComponentPropsWithRef } from 'styled-components'


import { theme } from 'styles/theme'
import { CheckboxCheckmarkIcon, DashIcon, IconProps, SvgIcons } from '../Icon/Icon'
import { Label } from '../Typography/Typography'

const CheckboxOuter = styled.div<WithIsDisabled>`
  text-align: start;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
`

const CheckedIcon = styled(CheckboxCheckmarkIcon)`
  transition: all ${theme.transitions.normal} ease-out;
  pointer-events: none;
  padding: 0.1ch;
  font-size: 1.5em;
  flex-shrink: 0;
  margin: auto 0;
  border: ${theme.borderWidths.thin} solid ${theme.colors.Gray9};
  color: ${theme.colors.Transparent};
`

const IndeterminateIcon = styled((props: IconProps) => <DashIcon {...props} />)`
  color: ${theme.colors.White};
  padding: 0.4ch;
  stroke-width: 0.2ch;
  position: absolute;
  margin: auto;
  inline-size: 1.5em;
  block-size: 1.5em;
  opacity: 0;
`

const RadioButtonIcon = styled(SvgIcons.RadioButtonSvg)``

const CheckboxLabelText = styled(Label)`
  color: ${theme.colors.MainBlack};
  padding-inline-start: inherit;
  pointer-events: none;
  text-transform: none;
`

interface SelectLabelProps extends WithIsDisabled, WithIsValid {}

const CheckboxLabel = styled(Label)<SelectLabelProps>`
  display: flex;
  align-items: center;
  padding: 0.8em;
  position: relative;
  transition: all ${theme.transitions.normal} ease-out;
  pointer-events: ${(props) => (props.isDisabled ? 'none' : 'all')};
  ${CheckedIcon}, ${IndeterminateIcon} {
    border-radius: ${theme.radii.small};
  }
  :hover {
    border-color: ${theme.colors.MainYellowOnHover};
    ${CheckedIcon}, ${IndeterminateIcon} {
      border-color: inherit;
    }
  }
  :focus-within {
    border-color: ${theme.colors.MainYellowOnHover};
    ${CheckedIcon}, ${IndeterminateIcon} {
      border-color: inherit;
    }
  }
`

const StyledInput = styled.input`
  display: none;
  :is(:indeterminate, :checked) ~ :is(${CheckedIcon}, ${IndeterminateIcon}) {
    color: ${theme.colors.MainBlack};
    border-color: ${theme.colors.MainYellow};
    background-color: ${theme.colors.MainYellow};
  }
  :is(:checked) ~ :is(${RadioButtonIcon}) .checked-circle {
    fill: ${theme.colors.MainYellow};
  }
  
  :indeterminate ~ ${CheckedIcon} {
    opacity: 0;
  }
  :indeterminate ~ ${IndeterminateIcon} {
    opacity: 1;
  }
  :focus {
    color: ${theme.colors.body};
  }
  :disabled ~ :is(${CheckedIcon}, ${IndeterminateIcon}) {
    border-color: ${theme.colors.TransparentMainBlack};
  }
  :disabled:checked ~ ${CheckedIcon} {
    border-color: transparent;
    background-color: ${theme.colors.MediumGrey};
  }
`

const ValidationMessageList = styled.ul`
  color: ${theme.colors.danger};
  padding: 0.8em;
  padding-inline-start: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6em;
  :empty {
    padding: 0;
  }
`

const ValidationMessage = styled.li``

interface CheckboxProps
  extends MaybeWithIsDisabled,
    MaybeWithLabel,
    MaybeWithIsRequired,
    MaybeWithValidationMessages,
    StyledComponentPropsWithRef<typeof StyledInput> {
  name: string
  isChecked: boolean
  type?: 'checkbox' | 'radio'
  isIndeterminate?: boolean
}

export function Checkbox({
  label,
  isChecked,
  title = label,
  children,
  isRequired = true,
  isDisabled = false,
  isIndeterminate = false,
  validationMessages = [],
  style = {},
  type = 'checkbox',
  className = '',
  ...props
}: CheckboxProps) {
  const isValid = validationMessages.length === 0
  return (
    <CheckboxOuter {...{ title, style, className, isDisabled }}>
      <CheckboxLabel {...{ isDisabled, isValid }}>
        <StyledInput
          {...{
            type,
            checked: isChecked,
            required: isRequired,
            disabled: isDisabled,
            ref: (el) => el && (el.indeterminate = isIndeterminate),
            ...props,
          }}
        />
        { type === 'checkbox' ? 
          <>
            <CheckedIcon />
            <IndeterminateIcon />
          </> :
          <RadioButtonIcon />
        }
        {children && <CheckboxLabelText {...{ as: 'div', children }} />}
      </CheckboxLabel>
      <ValidationMessageList>
        {validationMessages.map((message) => (
          <ValidationMessage {...{ key: message, children: message }} />
        ))}
      </ValidationMessageList>
    </CheckboxOuter>
  )
}
